// Effets et styles des marqueurs de dialogue ({boom}, {flash}, {éclair}, {noir}, {pause}, {tremble}…{/tremble},
// {gros}…{/gros}), joués par la boîte de dialogue et l'écran de combat pendant qu'ils écrivent le texte.
import { splitTextEffects, type TextEffect, type TextStyle } from '../../shared/text-effects.ts';
import { icon, richLength, richParts } from '../common/icons.ts';
import { el } from '../common/dom.ts';
import type { SoundBoard } from './sound.ts';

/** Écriture suspendue par {pause}. */
const PAUSE_MS = 1000;
const SHAKE_MS = 600;
const FLASH_MS = 450;
/** Fondu au noir : descente, noir complet, remontée. */
const BLACKOUT = { in: 350, hold: 500, out: 450 } as const;

/** Une unité d'écriture : un caractère, ou une icône entière. */
type Unit = string | { name: Parameters<typeof icon>[0] };

/**
 * Texte d'une page avec ses marqueurs, prêt à s'écrire lettre par lettre : `render(n)` donne les n premières unités
 * (styles compris), `reach(n)` joue les effets atteints et dit combien de temps l'écriture doit attendre.
 */
export class TypedText {
  readonly length: number;
  private readonly units: Unit[];
  /** Styles de chaque unité (vide : texte normal). */
  private readonly unitStyles: TextStyle[][];
  private readonly effects: { effect: TextEffect; at: number }[];
  private readonly sounds: SoundBoard;
  /** Fin (performance.now()) de la dernière {pause} jouée. */
  private pausedUntil = 0;

  constructor(raw: string, sounds: SoundBoard) {
    const { text, effects, styles } = splitTextEffects(raw);
    this.sounds = sounds;
    this.units = richParts(text).flatMap((part): Unit[] => (typeof part === 'string' ? [...part] : [part]));
    this.length = this.units.length;
    // Les positions du texte deviennent des positions d'unités (une icône compte pour un caractère).
    const unitAt = (index: number) => richLength(text.slice(0, index));
    this.effects = effects.map(({ effect, at }) => ({ effect, at: unitAt(at) }));
    this.unitStyles = this.units.map(() => []);
    for (const { style, from, to } of styles) {
      for (let i = unitAt(from); i < unitAt(to); i++) this.unitStyles[i]?.push(style);
    }
  }

  /** Les `count` premières unités, mises en forme (tout le texte par défaut). */
  render(count = this.length): (Node | string)[] {
    const nodes: (Node | string)[] = [];
    let i = 0;
    const end = Math.min(count, this.length);
    while (i < end) {
      const styles = this.unitStyles[i]!;
      const key = styles.join(' ');
      const group: Unit[] = [];
      while (i < end && this.unitStyles[i]!.join(' ') === key) group.push(this.units[i++]!);
      nodes.push(...(styles.length ? [styledGroup(group, styles)] : plainGroup(group)));
    }
    return nodes;
  }

  /** Joue les effets atteints quand `written` unités sont écrites ; renvoie l'attente à ajouter avant la suivante. */
  reach(written: number): number {
    let wait = 0;
    while (this.effects.length && this.effects[0]!.at <= written) wait += this.play(this.effects.shift()!.effect);
    return wait;
  }

  /** Caractère écrit à la place `index` (null : une icône). */
  charAt(index: number): string | null {
    const unit = this.units[index];
    return typeof unit === 'string' ? unit : null;
  }

  /** Temps restant (ms) de la {pause} en cours : l'avance rapide ne la raccourcit pas. */
  get pauseLeft(): number {
    return Math.max(0, this.pausedUntil - performance.now());
  }

  /**
   * Avance rapide : joue les effets jusqu'à la prochaine {pause} (qu'on ne peut pas passer) et renvoie le nombre
   * d'unités alors écrites — tout le texte s'il n'en reste plus.
   */
  skip(): number {
    const pause = this.effects.find(({ effect }) => effect === 'pause');
    if (!pause) {
      this.reach(Infinity);
      return this.length;
    }
    this.reach(pause.at);
    return pause.at;
  }

  private play(effect: TextEffect): number {
    const wait = playTextEffect(effect, this.sounds);
    if (effect === 'pause') this.pausedUntil = Math.max(this.pausedUntil, performance.now()) + wait;
    return wait;
  }
}

function plainGroup(units: Unit[]): (Node | string)[] {
  const nodes: (Node | string)[] = [];
  for (const unit of units) {
    if (typeof unit !== 'string') nodes.push(icon(unit.name));
    else if (typeof nodes.at(-1) === 'string') nodes[nodes.length - 1] += unit;
    else nodes.push(unit);
  }
  return nodes;
}

/** Mots mis en forme ; en tremblement, chaque lettre tremble à son propre rythme. */
function styledGroup(units: Unit[], styles: TextStyle[]): HTMLElement {
  const span = el('span', { className: styles.map((style) => `text-${style}`).join(' ') });
  if (!styles.includes('tremble')) {
    span.append(...plainGroup(units));
    return span;
  }
  units.forEach((unit, index) => {
    const letter = typeof unit === 'string' ? el('span', { textContent: unit }) : icon(unit.name);
    letter.style.setProperty('--i', String(index));
    span.append(letter);
  });
  return span;
}

/** Joue l'effet d'un marqueur ; renvoie le temps (ms) pendant lequel l'écriture du texte doit attendre. */
export function playTextEffect(effect: TextEffect, sounds: SoundBoard): number {
  switch (effect) {
    case 'boom':
      sounds.play('boom');
      shakeScreen();
      // Sur mobile, le téléphone tremble aussi.
      navigator.vibrate?.(200);
      return 0;
    case 'flash':
      overlay('white').animate([{ opacity: gentle() ? 0.45 : 0.95 }, { opacity: 0 }], { duration: FLASH_MS, easing: 'ease-out' });
      return 0;
    case 'éclair': {
      const peak = gentle() ? 0.4 : 0.95;
      overlay('white').animate([{ opacity: peak }, { opacity: 0, offset: 0.25 }, { opacity: peak * 0.8, offset: 0.4 }, { opacity: 0 }], { duration: 700, easing: 'ease-out' });
      sounds.play('thunder');
      navigator.vibrate?.([60, 80, 220]);
      return 0;
    }
    case 'noir': {
      const total = BLACKOUT.in + BLACKOUT.hold + BLACKOUT.out;
      overlay('black').animate([
        { opacity: 0 }, { opacity: 1, offset: BLACKOUT.in / total }, { opacity: 1, offset: (BLACKOUT.in + BLACKOUT.hold) / total }, { opacity: 0 },
      ], { duration: total, easing: 'ease-in-out' });
      // Le texte reprend quand l'écran revient.
      return total;
    }
    case 'pause':
      return PAUSE_MS;
  }
}

/** Le joueur a demandé moins d'animations (réglage de son appareil) : effets adoucis. */
const gentle = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

const overlays = new Map<string, HTMLElement>();

/** Calque plein écran d'une couleur, invisible au repos (animé par les effets). */
function overlay(color: 'white' | 'black'): HTMLElement {
  let layer = overlays.get(color);
  if (!layer) {
    layer = document.body.appendChild(el('div', { className: `screen-overlay ${color}` }));
    overlays.set(color, layer);
  }
  return layer;
}

let shakeTimer: ReturnType<typeof setTimeout> | undefined;

/** Secoue l'écran de jeu (carte, dialogue, combat) ; une nouvelle secousse relance l'animation. */
function shakeScreen(): void {
  const { body } = document;
  body.classList.remove('shaking');
  void body.offsetWidth; // relance l'animation CSS
  body.classList.add('shaking');
  clearTimeout(shakeTimer);
  shakeTimer = setTimeout(() => body.classList.remove('shaking'), SHAKE_MS);
}
